import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, AlertCircle, RotateCcw, Clock, Zap, ListChecks } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, ShimmerButton, SimHeader } from "@/components/sim/shared";
import { DownloadCoachingPdfButton } from "@/components/neusim/DownloadCoachingPdfButton";
import { useAttemptStore } from "@/store/attemptStore";
import type { ResultsResponse } from "@/types/simulation";

function bandToken(band: string | null): { label: string; color: string; bg: string } {
  const v = (band ?? "").toUpperCase();
  if (v.includes("HIGH") || v.includes("STRONG")) {
    return { label: band ?? "Strong", color: "#10B981", bg: "rgba(16,185,129,0.12)" };
  }
  if (v.includes("MEDIUM") || v.includes("MED") || v.includes("DEVELOP")) {
    return { label: band ?? "Developing", color: "#F59E0B", bg: "rgba(245,158,11,0.12)" };
  }
  if (v.includes("LOW") || v.includes("NEED")) {
    return { label: band ?? "Needs work", color: "#F87272", bg: "rgba(248,114,114,0.12)" };
  }
  return { label: band ?? "Complete", color: "#A78BFA", bg: "rgba(167,139,250,0.12)" };
}

function decisionBandToken(band: string | null): { color: string; bg: string } {
  const v = (band ?? "").toUpperCase();
  if (v.includes("HIGH") || v.includes("STRONG")) return { color: "#10B981", bg: "rgba(16,185,129,0.12)" };
  if (v.includes("MEDIUM") || v.includes("DEVELOP")) return { color: "#F59E0B", bg: "rgba(245,158,11,0.12)" };
  if (v.includes("LOW") || v.includes("NEED")) return { color: "#F87272", bg: "rgba(248,114,114,0.12)" };
  return { color: "#A78BFA", bg: "rgba(167,139,250,0.12)" };
}

function useCountUp(target: number, duration = 1100): number {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const t = Math.min((now - start) / duration, 1);
      setShown(Math.round(t * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return shown;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m === 0 ? `${s}s` : `${m}m ${String(s).padStart(2, "0")}s`;
}

/**
 * ResultsPage — the end-of-attempt scorecard. Uses the new sim design chrome
 * (ScreenShell + SimHeader + GlassCard) with an animated score ring (same
 * pattern as OutcomeScreen) for the headline, plus a per-decision breakdown.
 * Stays a standalone /results route so learners can revisit their scorecard.
 */
export function ResultsPage(): JSX.Element {
  const { simId, attemptId } = useParams<{ simId: string; attemptId: string }>();
  const navigate = useNavigate();
  const { fetchResults } = useAttemptStore();

  const [data, setData] = useState<ResultsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    if (attemptId === undefined) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchResults(attemptId);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load your results");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  const handleContinue = (): void => {
    if (data === null) return;
    // Always route through reflection — the sim's ReflectionScreenRequired
    // setting controls whether Skip is shown on that screen, NOT whether the
    // screen is shown at all. Skipping reflection is the learner's choice.
    navigate(`/sim/${simId}/attempt/${attemptId}/reflection`);
  };

  return (
    <ScreenShell>
      <SimHeader decision={data?.decisions.length ?? 0} xp={data?.totalXp ?? 0} />
      <main className="mx-auto max-w-4xl px-4 pb-20 md:px-8">
        {loading && (
          <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
              className="h-12 w-12 rounded-full border-2 border-white/10 border-t-fuchsia-400"
            />
            <p className="mt-5 text-sm text-white/60">Tallying your scorecard…</p>
          </div>
        )}

        {!loading && error !== null && (
          <GlassCard className="mx-auto mt-10 max-w-lg p-10 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-[#f87272]" />
            <h2 className="mt-4 text-lg font-semibold">We couldn&rsquo;t load your results</h2>
            <p className="mt-1 text-sm text-white/60">{error}</p>
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => void load()}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-2 text-sm font-medium text-white transition-colors hover:border-white/30 hover:bg-white/10"
              >
                <RotateCcw className="h-4 w-4" /> Try again
              </button>
            </div>
          </GlassCard>
        )}

        {!loading && error === null && data !== null && attemptId !== undefined && (
          <Scorecard data={data} attemptId={attemptId} onContinue={handleContinue} />
        )}
      </main>
    </ScreenShell>
  );
}

function Scorecard({ data, attemptId, onContinue }: { data: ResultsResponse; attemptId: string; onContinue: () => void }): JSX.Element {
  const animScore = useCountUp(data.totalScore);
  const percent = data.maxPossible > 0 ? Math.round((data.totalScore / data.maxPossible) * 100) : 0;
  const final = bandToken(data.finalBand);

  // SVG score ring (same shape as OutcomeScreen)
  const ringRadius = 64;
  const ringCirc = 2 * Math.PI * ringRadius;
  const dashLen = (percent / 100) * ringCirc;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
      {/* Hero card */}
      <GlassCard className="p-7 md:p-9">
        <div className="flex items-center gap-2 text-amber-300">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em]">🏆 Simulation complete</span>
        </div>
        <h1 className="mt-2 text-[28px] font-bold leading-tight">
          <GradientText>{data.simulationTitle}</GradientText>
        </h1>
        <p className="mt-1 text-sm text-white/55">
          {data.competencyName !== null ? `${data.competencyName} · ` : ""}
          {data.learnerName} · Attempt {data.attemptNo}
        </p>

        {/* Score ring + band */}
        <div className="mt-7 flex flex-col items-center gap-6 md:flex-row md:items-center md:gap-10">
          <div className="relative flex h-[160px] w-[160px] items-center justify-center">
            <svg viewBox="0 0 160 160" className="absolute inset-0 -rotate-90">
              <circle cx="80" cy="80" r={ringRadius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
              <motion.circle
                cx="80"
                cy="80"
                r={ringRadius}
                fill="none"
                stroke={final.color}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={`${ringCirc} ${ringCirc}`}
                initial={{ strokeDashoffset: ringCirc }}
                animate={{ strokeDashoffset: ringCirc - dashLen }}
                transition={{ duration: 1.2, ease: "easeOut" }}
              />
            </svg>
            <div className="text-center">
              <div className="font-mono text-4xl font-bold text-white">{animScore}</div>
              <div className="text-[11px] text-white/45">/ {data.maxPossible}</div>
              <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">{percent}%</div>
            </div>
          </div>

          <div className="flex-1 space-y-3">
            {data.finalBand !== null && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Final band</div>
                <span
                  className="mt-1.5 inline-block rounded-full px-4 py-1.5 text-sm font-semibold uppercase tracking-[0.12em]"
                  style={{ background: final.bg, border: `1px solid ${final.color}80`, color: final.color }}
                >
                  {final.label}
                </span>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <Stat icon={<Zap className="h-4 w-4 text-cyan-300" />} label="XP earned" value={`${data.totalXp}`} />
              <Stat icon={<Clock className="h-4 w-4 text-fuchsia-300" />} label="Time" value={formatDuration(data.durationSeconds)} />
              <Stat icon={<ListChecks className="h-4 w-4 text-violet-300" />} label="Decisions" value={`${data.decisions.length}`} />
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Decision breakdown */}
      {data.decisions.length > 0 && (
        <GlassCard className="mt-6 p-6 md:p-7">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-fuchsia-200">Decision breakdown</h2>
          <ul className="mt-4 space-y-2">
            {data.decisions.map((d, i) => {
              const c = decisionBandToken(d.band);
              return (
                <motion.li
                  key={`${d.sequenceNo}-${i}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.05 }}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-xs font-semibold text-white/60">
                    {d.sequenceNo}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white/90">{d.title ?? `Decision ${d.sequenceNo}`}</p>
                    <p className="text-[10px] uppercase tracking-[0.12em] text-white/40">
                      {d.questionType === "MultipleChoice" ? "Multiple choice" : "Open text"}
                    </p>
                  </div>
                  {d.band !== null && (
                    <span
                      className="hidden rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] sm:inline"
                      style={{ background: c.bg, border: `1px solid ${c.color}66`, color: c.color }}
                    >
                      {d.band}
                    </span>
                  )}
                  <span className="shrink-0 font-mono text-base font-bold" style={{ color: c.color }}>
                    {d.score}
                    <span className="text-xs font-normal text-white/30">/10</span>
                  </span>
                </motion.li>
              );
            })}
          </ul>
        </GlassCard>
      )}

      <div className="mt-8 flex flex-wrap items-center justify-end gap-3">
        <DownloadCoachingPdfButton attemptId={attemptId} label="Download report" />
        <ShimmerButton onClick={onContinue}>
          Continue to reflection <ArrowRight className="h-4 w-4" />
        </ShimmerButton>
      </div>
    </motion.div>
  );
}

function Stat({ icon, label, value }: { icon: JSX.Element; label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-white/40">{label}</span>
      </div>
      <p className="mt-0.5 text-base font-semibold text-white">{value}</p>
    </div>
  );
}
